import React, { FC, MouseEvent } from 'react';
import type { TokenColor } from '../../setup';
import { IconProps } from '../Icon';
import { BpDesignSystem } from '../../types';
import { usePrevious } from '../../utils/useCompare';
import { IconButton } from '../IconButton/IconButton';
import { css, styled } from '../../styles';
import { Badge } from '../Badge/Badge';

export type NotificationBellProps = BpDesignSystem.PropsWithChildren<{
  unReadCount: number;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  bellColor?: TokenColor;
  notificationBubbleColor?: 'primary' | 'secondary' | 'default' | 'error';
  maxNotifications?: number;
  customIconName?: IconProps['name'];
  tooltipText?: string;
}>;

export const NotificationBell: FC<NotificationBellProps> = ({
  unReadCount,
  onClick,
  bellColor,
  notificationBubbleColor = 'error',
  maxNotifications = 99,
  customIconName,
  dataTest,
  tooltipText,
  ...props
}) => {
  const previousUnReadCount = usePrevious(unReadCount) ?? 0;

  // Only enable animation for new notifications. if user mark all as read, animation should not trigger
  const enableAnimation = unReadCount && unReadCount > 0 && unReadCount >= previousUnReadCount;

  // Trigger animation when there is a new notification
  const animationTrigger = unReadCount ? unReadCount.toString() : '';

  return (
    <StyledBadge
      $enableAnimation={!!enableAnimation}
      $animationTrigger={animationTrigger}
      badgeContent={unReadCount}
      max={maxNotifications}
      dataTest="notification-badge"
      color={notificationBubbleColor}
      {...props}
    >
      <StyledBell
        $enableAnimation={!!enableAnimation}
        $animationTrigger={animationTrigger}
        onClick={onClick}
        dataTest={dataTest}
        slotProps={{
          tooltip: {
            title: tooltipText
          }
        }}
        iconName={customIconName ?? 'notifications'}
        style={{ fill: bellColor }}
        color={bellColor}
      />
    </StyledBadge>
  );
};

const StyledBell = styled(IconButton)<{ $enableAnimation: boolean; $animationTrigger: string }>`
  // set new animation when there is a new notification to force it to play
  ${({ $animationTrigger }) =>
    $animationTrigger &&
    css`
      @keyframes new-notification-for-bell-${$animationTrigger} {
        0% {
          transform: rotate(45deg);
        }
        25% {
          transform: rotate(-45deg);
        }
        50% {
          transform: rotate(45deg);
        }
        100% {
          transform: rotate(-45deg);
        }
      }
    `}

  animation-timing-function: ease-in-out;
  animation-name: ${({ $enableAnimation, $animationTrigger }) =>
    $enableAnimation ? `new-notification-for-bell-${$animationTrigger}` : ''};
  animation-duration: 480ms;
`;

const StyledBadge = styled(Badge)<{ $enableAnimation: boolean; $animationTrigger: string }>`
  .MuiBadge-badge {
       // set new animation when there is a new notification to force it to play
    ${({ $animationTrigger }) =>
      $animationTrigger &&
      css`
      @keyframes new-notification-for-badge-${$animationTrigger} {
       0% {
        opacity: 0;
        transform: scale(0) translate(-100%, 100%);
      }
      100% {
        opacity: 1;
        transform: scale(1) translate(18%, 13%);
        }
      }
    `}
      pointer-events: none;
      animation-timing-function: ease-in-out;
      animation-fill-mode: both;
      transform-origin: 50% 50%;
      opacity: 0;
      animation-name: ${({ $enableAnimation, $animationTrigger }) =>
        $enableAnimation ? `new-notification-for-badge-${$animationTrigger}` : ''};
      transform: scale(1) translate(18%, 13%);
      animation-delay: 480ms;
      animation-duration: 120ms;
    }
`;
